import { useState, forwardRef } from "react";

interface CurrencyIconProps {
  currency: string;
  size?: number;
  className?: string;
}

// Fiat: map currency code → country code for flagcdn.com
const FIAT_FLAGS: Record<string, string> = {
  USD: "us", EUR: "eu", GBP: "gb", CHF: "ch", CAD: "ca", AUD: "au",
  JPY: "jp", CNY: "cn", INR: "in", BRL: "br", MXN: "mx", SGD: "sg",
  HKD: "hk", NOK: "no", SEK: "se", DKK: "dk", NZD: "nz", ZAR: "za",
  KRW: "kr", TRY: "tr", PLN: "pl", THB: "th", IDR: "id", HUF: "hu",
  CZK: "cz", ILS: "il", PHP: "ph", AED: "ae", SAR: "sa", MYR: "my",
  NGN: "ng", KES: "ke", GHS: "gh", EGP: "eg", PKR: "pk", BDT: "bd",
  VND: "vn", CLP: "cl", COP: "co", PEN: "pe", ARS: "ar", UAH: "ua",
  RON: "ro", BGN: "bg", HRK: "hr", RSD: "rs", TWD: "tw", QAR: "qa",
  KWD: "kw", BHD: "bh", OMR: "om", JOD: "jo", LBP: "lb", MAD: "ma",
  TND: "tn", XOF: "sn", XAF: "cm", XPF: "pf", XCD: "ag", XCG: "cw", ZWG: "zw",
  // Extended ISO 4217 mappings
  AFN: "af", ALL: "al", AMD: "am", ANG: "cw", AOA: "ao", AWG: "aw",
  AZN: "az", BAM: "ba", BBD: "bb", BIF: "bi", BMD: "bm", BND: "bn",
  BOB: "bo", BSD: "bs", BTN: "bt", BWP: "bw", BYN: "by", BZD: "bz",
  CDF: "cd", CRC: "cr", CUP: "cu", CVE: "cv", DJF: "dj", DOP: "do",
  DZD: "dz", ERN: "er", ETB: "et", FJD: "fj", FKP: "fk", GEL: "ge",
  GIP: "gi", GMD: "gm", GNF: "gn", GTQ: "gt", GYD: "gy", HNL: "hn",
  HTG: "ht", IQD: "iq", IRR: "ir", ISK: "is", JMD: "jm", KGS: "kg",
  KHR: "kh", KMF: "km", KPW: "kp", KYD: "ky", KZT: "kz", LAK: "la",
  LKR: "lk", LRD: "lr", LSL: "ls", LYD: "ly", MDL: "md", MGA: "mg",
  MKD: "mk", MMK: "mm", MNT: "mn", MOP: "mo", MRU: "mr", MUR: "mu",
  MVR: "mv", MWK: "mw", MZN: "mz", NAD: "na", NIO: "ni", NPR: "np",
  PAB: "pa", PGK: "pg", PYG: "py", RUB: "ru", RWF: "rw", SBD: "sb",
  SCR: "sc", SDG: "sd", SHP: "sh", SLE: "sl", SOS: "so", SRD: "sr",
  SSP: "ss", STN: "st", SVC: "sv", SYP: "sy", SZL: "sz", TJS: "tj",
  TMT: "tm", TOP: "to", TTD: "tt", TZS: "tz", UGX: "ug", UYU: "uy",
  UZS: "uz", VES: "ve", VUV: "vu", WST: "ws", YER: "ye",
  ZMW: "zm", ZWL: "zw",
  // Additional / legacy codes
  CUC: "cu", SLL: "sl", STD: "st", ZWD: "zw", MRO: "mr", VEF: "ve",
};

// ── Crypto inline SVGs ──

function BtcSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#F7931A"/>
      <path d="M22.5 13.6c.3-2-1.2-3.1-3.3-3.8l.7-2.7-1.6-.4-.7 2.7c-.4-.1-.9-.2-1.3-.3l.7-2.7-1.7-.4-.7 2.7c-.3-.1-.7-.2-1-.2l-2.3-.6-.4 1.8s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.2c0 0 .1 0 .1 0l-.1 0-1.1 4.5c-.1.2-.3.5-.8.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.2.5c.4.1.8.2 1.2.3l-.7 2.8 1.6.4.7-2.8c.4.1.9.2 1.3.3l-.7 2.7 1.6.4.7-2.8c3 .6 5.2.3 6.2-2.4.7-2.1 0-3.4-1.6-4.2 1.1-.3 2-.1 2.2-2.1zM19.2 19c-.5 2.1-4 1-5.2.7l.9-3.7c1.1.3 4.8.8 4.3 3zm.6-5.5c-.5 1.9-3.4.9-4.3.7l.8-3.4c1 .2 4 .7 3.5 2.7z" fill="white"/></svg>
  );
}

function EthSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#627EEA"/>
      <path d="M16 4v8.9l7.5 3.3L16 4z" fill="white" fillOpacity="0.6"/><path d="M16 4L8.5 16.2l7.5-3.3V4z" fill="white"/>
      <path d="M16 21.9v6.1l7.5-10.4L16 21.9z" fill="white" fillOpacity="0.6"/><path d="M16 28v-6.1l-7.5-4.3L16 28z" fill="white"/>
      <path d="M16 20.6l7.5-4.4L16 13v7.6z" fill="white" fillOpacity="0.2"/><path d="M8.5 16.2l7.5 4.4V13l-7.5 3.2z" fill="white" fillOpacity="0.6"/></svg>
  );
}

function UsdtSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#26A17B"/>
      <path d="M17.9 17.1v0c-.1 0-.7.1-2 .1-1 0-1.7 0-1.9-.1v0c-3.8-.2-6.6-.9-6.6-1.7s2.8-1.6 6.6-1.7v2.7c.3 0 .9.1 2 .1 1.3 0 1.8-.1 1.9-.1v-2.7c3.8.2 6.6.9 6.6 1.7s-2.8 1.6-6.6 1.7zm0-3.7v-2.4h5.4V7.8H8.8v3.2h5.4v2.4c-4.3.2-7.5 1.1-7.5 2.2s3.2 2 7.5 2.2v7.9h3.7v-7.9c4.3-.2 7.5-1.1 7.5-2.2s-3.2-2-7.5-2.2z" fill="white"/></svg>
  );
}

function TrxSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#EF0027"/>
      <path d="M8 9.4l3.8 14.8L24 12.4l-6-3H8zm2.2 1.4h6.6l-5.2 3.8L10.2 10.8zm1.2 5.6l5.6-4.2-1 8.4-4.6-4.2z" fill="white"/></svg>
  );
}

function SolSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#9945FF"/>
      <path d="M9.5 20.2a.5.5 0 01.4-.2h13.8c.2 0 .4.3.2.5l-2.4 2.4a.5.5 0 01-.4.2H7.3c-.2 0-.4-.3-.2-.5l2.4-2.4zm0-11.3a.5.5 0 01.4-.2h13.8c.2 0 .4.3.2.5l-2.4 2.4a.5.5 0 01-.4.2H7.3c-.2 0-.4-.3-.2-.5L9.5 8.9zm12.1 3.6a.5.5 0 00-.4-.2H7.3c-.2 0-.4.3-.2.5l2.4 2.4a.5.5 0 00.4.2h13.8c.2 0 .4-.3.2-.5l-2.3-2.4z" fill="white"/></svg>
  );
}

function BnbSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#F3BA2F"/>
      <path d="M12.1 14.3L16 10.4l3.9 3.9 2.3-2.3L16 5.8 9.8 12l2.3 2.3zm-6.3 1.7l2.3-2.3 2.3 2.3-2.3 2.3-2.3-2.3zm6.3 1.7L16 21.6l3.9-3.9 2.3 2.3L16 26.2 9.8 20l2.3-2.3zm8.6-1.7l2.3-2.3 2.3 2.3-2.3 2.3-2.3-2.3zM18.8 16L16 13.2 13.2 16 16 18.8 18.8 16z" fill="white"/></svg>
  );
}

function XrpSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#23292F"/>
      <path d="M10.2 8.8h2.6l3.2 3.5 3.2-3.5h2.6l-4.5 5L16 15l-1.3-1.2-4.5-5zm0 14.4h2.6l3.2-3.5 3.2 3.5h2.6l-4.5-5L16 17l-1.3 1.2-4.5 5z" fill="white"/></svg>
  );
}

function LtcSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#345D9D"/>
      <path d="M15.5 9h3l-2.5 8.5 2 -.7-.4 1.5-2 .7L14.5 23h8l-.5 1.5H10l1.5-5.5-2 .7.4-1.5 2-.7L15.5 9z" fill="white"/></svg>
  );
}

function AdaSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#0033AD"/>
      <circle cx="16" cy="11" r="2" fill="white"/><circle cx="16" cy="21" r="2" fill="white"/>
      <circle cx="11.5" cy="13.5" r="1.5" fill="white"/><circle cx="20.5" cy="13.5" r="1.5" fill="white"/>
      <circle cx="11.5" cy="18.5" r="1.5" fill="white"/><circle cx="20.5" cy="18.5" r="1.5" fill="white"/></svg>
  );
}

function DogeSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#C2A633"/>
      <path d="M13 10h4c4.4 0 6 2.5 6 6s-1.6 6-6 6h-4V10zm2.5 2.2v7.6h1.2c2.8 0 3.8-1.5 3.8-3.8s-1-3.8-3.8-3.8h-1.2zM12 15.5h5v1.5h-5v-1.5z" fill="white"/></svg>
  );
}

function AvaxSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#E84142"/>
      <path d="M20.8 21h3L16 9l-3.2 5.8h2.9L16 14.6l4.8 6.4zm-9.6 0h3.3l2.2-3.7-1.5-2.6L11.2 21z" fill="white"/></svg>
  );
}

function DotSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#E6007A"/>
      <circle cx="16" cy="9" r="3" fill="white"/><circle cx="16" cy="23" r="3" fill="white"/>
      <ellipse cx="16" cy="16" rx="5" ry="3" fill="none" stroke="white" strokeWidth="1.8"/></svg>
  );
}

function MaticSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#8247E5"/>
      <path d="M20.3 13.3a1 1 0 00-1 0l-2.4 1.4-1.6.9-2.4 1.4a1 1 0 01-1 0l-1.9-1.1a1 1 0 01-.5-.9v-2.1a1 1 0 01.5-.9l1.9-1.1a1 1 0 011 0l1.9 1.1a1 1 0 01.5.9v1.4l1.6-.9v-1.4a1 1 0 00-.5-.9L13.5 9a1 1 0 00-1 0l-2.9 1.7a1 1 0 00-.5.9v3.3a1 1 0 00.5.9l2.8 1.6a1 1 0 001 0l2.4-1.4 1.6-.9 2.4-1.4a1 1 0 011 0l1.9 1.1a1 1 0 01.5.9v2.1a1 1 0 01-.5.9l-1.9 1.1a1 1 0 01-1 0l-1.9-1.1a1 1 0 01-.5-.9v-1.4l-1.6.9v1.4a1 1 0 00.5.9l2.8 1.6a1 1 0 001 0l2.8-1.6a1 1 0 00.5-.9v-3.3a1 1 0 00-.5-.9l-2.8-1.7z" fill="white"/></svg>
  );
}

function LinkSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#2A5ADA"/>
      <path d="M16 7l-2 1.2-5 2.9L7 12.3v7.4l2 1.2 5 2.9 2 1.2 2-1.2 5-2.9 2-1.2v-7.4l-2-1.2-5-2.9L16 7zm-5 13.5v-9l5-2.9 5 2.9v9l-5 2.9-5-2.9z" fill="white"/></svg>
  );
}

function UsdcSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#2775CA"/>
      <path d="M20.5 18.5c0-2.1-1.3-2.8-3.8-3.1-1.8-.3-2.1-.7-2.1-1.5s.7-1.3 1.9-1.3c1.1 0 1.7.4 2 1.3l.1.2h1.7l-.1-.3c-.3-1.4-1.4-2.3-2.8-2.6v-1.6h-2v1.5c-1.7.3-2.8 1.5-2.8 3 0 2 1.3 2.7 3.8 3 1.7.3 2.1.8 2.1 1.6 0 1-.8 1.5-2.1 1.5-1.5 0-2.1-.6-2.3-1.6l-.1-.2h-1.7l.1.3c.3 1.6 1.3 2.6 3 2.9v1.6h2v-1.6c1.8-.3 2.9-1.5 2.9-3.1z" fill="white"/></svg>
  );
}

function TonSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#0098EA"/>
      <path d="M10 11h12l-6 13-6-13zm1.8 1.5l4.2 9.3 4.2-9.3H11.8z" fill="white"/></svg>
  );
}

const CRYPTO_COMPONENTS: Record<string, React.FC<{ size: number }>> = {
  BTC: BtcSvg, ETH: EthSvg, USDT: UsdtSvg, TRX: TrxSvg,
  SOL: SolSvg, BNB: BnbSvg, XRP: XrpSvg, LTC: LtcSvg,
  ADA: AdaSvg, DOGE: DogeSvg, AVAX: AvaxSvg, DOT: DotSvg,
  MATIC: MaticSvg, POL: MaticSvg, LINK: LinkSvg, USDC: UsdcSvg, TON: TonSvg,
  XTEM: ({ size }: { size: number }) => (
    <img src="/xtem-coin.png" alt="XTEM V2" width={size} height={size} className="rounded-full object-cover" style={{ width: size, height: size }} />
  ),
};

const FALLBACK_COLORS: Record<string, string> = {
  BTC: "#F7931A", ETH: "#627EEA", USDT: "#26A17B", TRX: "#EF0027",
  SOL: "#9945FF", BNB: "#F3BA2F", XRP: "#23292F", LTC: "#345D9D",
  ADA: "#0033AD", DOGE: "#C2A633", AVAX: "#E84142", DOT: "#E6007A",
  MATIC: "#8247E5", POL: "#8247E5", LINK: "#2A5ADA", USDC: "#2775CA", TON: "#0098EA", XTEM: "#D4A017",
  USD: "#22C55E", EUR: "#2563EB", GBP: "#7C3AED", CHF: "#DC2626",
  CAD: "#EF4444", AUD: "#F59E0B", JPY: "#6366F1",
};

const FALLBACK_SYMBOLS: Record<string, string> = {
  BTC: "₿", ETH: "Ξ", USDT: "₮", TRX: "T",
  USD: "$", EUR: "€", GBP: "£", CHF: "Fr", CAD: "C$", AUD: "A$", JPY: "¥",
  XTEM: "XV",
};

const CurrencyIcon = forwardRef<HTMLElement, CurrencyIconProps>(
  ({ currency, size = 20, className = "" }, ref) => {
    const upper = currency.toUpperCase();
    const [imgError, setImgError] = useState(false);

    const CryptoComp = CRYPTO_COMPONENTS[upper];
    if (CryptoComp) {
      return (
        <span ref={ref as React.Ref<HTMLSpanElement>} className={`inline-block shrink-0 ${className}`}>
          <CryptoComp size={size} />
        </span>
      );
    }

    const countryCode = FIAT_FLAGS[upper];
    if (countryCode && !imgError) {
      const w = Math.max(40, Math.ceil(size * 2));
      return (
        <img
          ref={ref as React.Ref<HTMLImageElement>}
          src={`https://flagcdn.com/w${w >= 80 ? 80 : 40}/${countryCode}.png`}
          alt={`${upper} flag`}
          width={size}
          height={size}
          loading="lazy"
          onError={() => setImgError(true)}
          className={`inline-block shrink-0 rounded-full object-cover ${className}`}
          style={{ width: size, height: size }}
        />
      );
    }

    const color = FALLBACK_COLORS[upper] ?? "#9CA3AF";
    const symbol = FALLBACK_SYMBOLS[upper] ?? upper.slice(0, 2);
    return (
      <svg ref={ref as unknown as React.Ref<SVGSVGElement>} width={size} height={size} viewBox="0 0 32 32" fill="none" className={`inline-block shrink-0 ${className}`}>
        <circle cx="16" cy="16" r="16" fill={color} />
        <text x="16" y="17" textAnchor="middle" dominantBaseline="central"
          fill="white" fontWeight="700" fontSize="14" fontFamily="system-ui, -apple-system, sans-serif">
          {symbol}
        </text>
      </svg>
    );
  }
);

CurrencyIcon.displayName = "CurrencyIcon";

export default CurrencyIcon;
